//VietnamDataDisplay.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { loadData } from './DataLoader';
import { Table, TableBody, TableCell, TableHead, TableRow, Paper, Typography, Select, MenuItem, Container, SelectChangeEvent, FormControl, InputLabel, styled, TextField, Grid } from '@mui/material';
import { black } from 'material-ui/styles/colors';

const StyledContainer = styled(Container)({
  marginTop: '0px',
  padding: '20px',
  marginBottom: '60px',
});

const StyledNote = styled('div')({
  padding: '15px',
  border: '1px solid #ddd',
  borderRadius: '5px',
  backgroundColor: 'rgba(249, 249, 249, 0.5)',
  fontStyle: 'italic',
  fontSize: '14px',
  lineHeight: '1.5',
  color: 'black',
});

const StyledPaper = styled(Paper)({
  marginTop: '20px',
  backgroundColor: 'transparent', // Set background color to transparent
  overflowX: 'auto', // Add this if you want to handle overflowing content
});


// Update the StyledFormControl to be full width
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: '20px',
  width: '100%', // Use full width for small devices
  [theme.breakpoints.up('sm')]: {
    // Keep original styles for widths above the sm breakpoint
    '&:first-child': {
      marginRight: '10px',
    },
    minWidth: 120,
  },
}));


const StyledTableRow = styled(TableRow)({
  backgroundColor: 'rgba(249, 249, 249, 0.1)', // Semi-transparent white background
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)', // Slightly more opaque on hover
  },
});


const StyledTableCell = styled(TableCell)({
  borderBottom: 'none',
  wordWrap: 'break-word',
  maxWidth: '130px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'black',
});

const StyledHeaderTableCell = styled(TableCell)({
  borderBottom: 'none',
  fontWeight: 'bold',
  fontSize: '1.2em',
  wordWrap: 'break-word',
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  backgroundColor: 'rgba(249, 249, 249, 0.1)',
});

const StyledSearchFormControl = styled(FormControl)({
  marginBottom: '20px',
  marginRight: '10px',
  minWidth: 120,
});

const StyledGridContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

interface IData {
  insideData: any;
  outsideData: any;
}

// Assuming the type for the structure of `selectedData` is something like this:
interface ICategoryData {
  [key: string]: any; // The type here should be more specific based on the actual data structure.
}

interface ISelectedData {
  [key: string]: ICategoryData;
}

// Function to transform category keys into human-readable strings
const transformCategory = (category: string) => {
  const map: { [key: string]: string } = {

    "default-update": "Last Update",
    "visitor_inside_canada": "Visitor Inside Canada",
    "visitor_extension": "Visitor Extension",
    "study_permit_outside": "Study Permit Outside",
    "study_extension": "Study Extension",
    "work_extension_new_employer": "Work Extension New Employer",
    "iec": "International Experience Canada",
    "eta": "Electronic Travel Authorization",
    "atlantic_immigration": "Atlantic Immigration",
    "cec": "Canadian Experience Class Express Entry",
    "quebec_business": "Quebec Business",
    "provincial_nominees_back": "Provincial Nominees Backlog",
    "self_employed_federal": "Self Employed Federal",
    "fed_skilled_workers": "Federal Skilled Workers",
    "fed_skilled_trades": "Federal Skilled Trades",
    "skilled_workers_quebec": "Skilled Workers Quebec",
    "startup_back": "Startup Backlog",
    "spousal_canada_qc": "Spousal Sponsorship Canada Quebec",
    "spousal_canada_roc": "Spousal Sponsorship Canada Rest of Canada",
    "spousal_abroad_qc": "Spousal Sponsorship Abroad Quebec",
    "spousal_abroad_roc": "Spousal Sponsorship Abroad Rest of Canada",
    "pgp_qc": "Parents and Grandparents Program Quebec",
    "pgp_roc": "Parents and Grandparents Program Rest of Canada",
    "refugees_protected_back": "Refugees and Protected Persons Backlog",
    "humanitarian_compassionate_back": "Humanitarian and Compassionate Grounds Backlog",
    "citizenship": "Citizenship",
    "pr_card": "Permanent Resident Card",
    "rep_documents": "Replacement Documents",
    "caregivers": "Caregivers Program",
    "sawp": "Seasonal Agricultural Worker Program Current Processing Times",
    "dependents_protected_persons": "Dependents of Protected Persons in Canada Class Processing Times",
    "atlantic-immigration-program": "Atlantic Immigration Program",
    "adopted_child_global": "Adopted Child Global",
    "dependent_child_global": "Dependent Child Global",
    "supervisa_global": "Super Visa Global",
    "gar_global": "Government-Assisted Refugees Global",
    "psr_global": "Private Sponsorship of Refugees Global",
    "psr_sponsorship": "Private Sponsorship of Refugees",
    "psr_refugee": "Privately Sponsored Refugees",
    "cec_flpt": "Canadian Experience Class Federal Live-in Caregiver Program",
    "pnp_ee_flpt": "Provincial Nominee Program Express Entry Federal Live-in Caregiver Program",
    "pnp_flpt": "Provincial Nominee Program Federal Live-in Caregiver Program",
    "fsw_ee_flpt": "Federal Skilled Workers Express Entry Federal Live-in Caregiver Program",
    "skilled_workers_qc_flpt": "Skilled Workers Quebec Federal Live-in Caregiver Program",
    "cit_grant_flpt": "Citizenship Grant Federal Live-in Caregiver Program",
    "cit_proof_flpt": "Citizenship Proof Federal Live-in Caregiver Program",
    "spousal_canada_qc_flpt": "Spousal Sponsorship Canada Quebec Federal Live-in Caregiver Program",
    "spousal_canada_roc_flpt": "Spousal Sponsorship Canada Rest of Canada Federal Live-in Caregiver Program",
    "spousal_outside_qc_flpt": "Spousal Sponsorship Outside Quebec Federal Live-in Caregiver Program",
    "spousal_outside_roc_flpt": "Spousal Sponsorship Outside Rest of Canada Federal Live-in Caregiver Program",
    "pgp_qc_flpt": "Parents and Grandparents Program Quebec Federal Live-in Caregiver Program",
    "pgp_roc_flpt": "Parents and Grandparents Program Rest of Canada Federal Live-in Caregiver Program",
    "trv_flpt": "Temporary Resident Visa Federal Live-in Caregiver Program",
    "sp_flpt": "Study Permit Federal Live-in Caregiver Program",
    "wp_flpt": "Work Permit Federal Live-in Caregiver Program",
    "visitor_outside_canada": "Visitor Outside Canada",
    "supervisa": "Super Visa",
    "study": "Study Permit",
    "work": "Work Permit",
    "child_dependent": "Child Dependent",
    "child_adopted": "Child Adopted",
    "refugees_gov": "Government-Assisted Refugees",
    "refugees_private": "Private Sponsorship of Refugees",
  };

  return map[category] || category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};


const VietnamDataDisplay: React.FC = () => {
  const [data, setData] = useState<{ insideData: ISelectedData; outsideData: ISelectedData } | null>(null);
  const [selectedOption, setSelectedOption] = useState('Inside');
  const [selectedCountry, setSelectedCountry] = useState('VN');
  const [searchTerm, setSearchTerm] = useState('');


  const renderCellValue = (cellData: any) => {
    if (typeof cellData === 'object') {
      return Object.keys(cellData)
        .map((key) => {
          const value = cellData[key];
          let formattedKey = key.replace(/FLPT|_/g, ' ').trim();
          if ([
            "skilled_workers_qc_flpt", "fsw_ee_flpt", "pnp_flpt", "pnp_ee_flpt",
            "cec_flpt", "gar_global", "supervisa_global", "dependent_child_global",
            "adopted_child_global", "atlantic-immigration-program", "dependents_protected_persons",
            "sawp_current", "humanitarian_compassionate_back", "refugees_protected_past",
            "pgp_roc", "pgp_qc", "spousal_abroad_roc", "spousal_abroad_qc",
            "spousal_canada_roc", "spousal_canada_qc", "startup_back", "skilled_workers_quebec",
            "skilled_trades_ee", "self_employed_federal", "skilled_workers_ee",
            "study_permit_outside", "iec", "eta", "atlantic_immigration", "cec_ee", "quebec_business",
            "cit_grant_flpt", "cit_proof_flpt", "spousal_canada_qc_flpt", "spousal_canada_roc_flpt",
            "spousal_outside_qc_flpt", "spousal_outside_roc_flpt", "pgp_qc_flpt", "pgp_roc_flpt",
            "trv_flpt", "sp_flpt", "wp_flpt"
          ].includes(key)) {
            formattedKey = "";
          }
          return formattedKey ? `${formattedKey}: ${value}` : `${value}`;
        })
        .join(', ')
        .replace(/lastupdated: /g, "");
    }
    return cellData;
  };

  useEffect(() => {
    loadData().then((loadedData) => {
      setData(loadedData);
    });
  }, []);

  const handleOptionChange = (event: SelectChangeEvent) => {
    setSelectedOption(event.target.value as string);
    setSearchTerm(''); // Reset search term on option change
  };

  const handleCountryChange = (event: SelectChangeEvent) => {
    setSelectedCountry(event.target.value as string);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Make sure to handle the case when `selectedData` is not set or empty.
  const selectedData: ISelectedData = data ? (selectedOption === 'Inside' ? data.insideData : data.outsideData) : {};

  // useMemo to memoize filtered data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return selectedData;

    return Object.keys(selectedData).reduce((acc, key) => {
      if (transformCategory(key).toLowerCase().includes(searchTerm.toLowerCase())) {
        acc[key] = selectedData[key];
      }
      return acc;
    }, {} as ISelectedData);
  }, [selectedData, searchTerm]);

  let lastUpdatedDate: string = "Not available";

  if (data && data.outsideData && 'visitor-outside-canada' in data.outsideData) {
    const outsideData = data.outsideData['visitor-outside-canada'];
    if (typeof outsideData === 'object' && outsideData !== null && 'lastupdated' in outsideData) {
      lastUpdatedDate = outsideData['lastupdated'] || "Not available";
    }
  }

  console.log("Last updated:", lastUpdatedDate);


  if (!data) return <div>Loading...</div>;


  return (
    <StyledContainer maxWidth="md">
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={6}>
      <StyledFormControl variant="outlined">
      <InputLabel id="option-label">Option</InputLabel>
            <Select
              labelId="option-label"
              value={selectedOption}
              onChange={handleOptionChange}
              label="Option"
              fullWidth
            >
          <MenuItem value={'Inside'}>Inside Canada</MenuItem>
          <MenuItem value={'Outside'}>Outside Canada</MenuItem>
        </Select>
      </StyledFormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
      {selectedOption === 'Outside' ? (
        <StyledFormControl variant="outlined" fullWidth>
          <InputLabel id="country-label">Country</InputLabel>
              <Select
                labelId="country-label"
                value={selectedCountry}
                onChange={handleCountryChange}
                label="Country"
                fullWidth
              >
            <MenuItem value={'VN'}>Vietnam</MenuItem>
            <MenuItem value={'AF'}>Afghanistan</MenuItem>
            <MenuItem value={'AL'}>Albania</MenuItem>
            <MenuItem value={'DZ'}>Algeria</MenuItem>
            <MenuItem value={'AD'}>Andorra</MenuItem>
            <MenuItem value={'AO'}>Angola</MenuItem>
            <MenuItem value={'AI'}>Anguilla</MenuItem>
            <MenuItem value={'AG'}>Antigua and Barbuda</MenuItem>
            <MenuItem value={'AR'}>Argentina</MenuItem>
            <MenuItem value={'AM'}>Armenia</MenuItem>
            <MenuItem value={'AW'}>Aruba</MenuItem>
            <MenuItem value={'AU'}>Australia</MenuItem>
            <MenuItem value={'AT'}>Austria</MenuItem>
            <MenuItem value={'AZ'}>Azerbaijan</MenuItem>
            <MenuItem value={'BS'}>Bahamas</MenuItem>
            <MenuItem value={'BH'}>Bahrain</MenuItem>
            <MenuItem value={'BD'}>Bangladesh</MenuItem>
            <MenuItem value={'BB'}>Barbados</MenuItem>
            <MenuItem value={'BY'}>Belarus</MenuItem>
            <MenuItem value={'BE'}>Belgium</MenuItem>
            <MenuItem value={'BZ'}>Belize</MenuItem>
            <MenuItem value={'BJ'}>Benin</MenuItem>
            <MenuItem value={'BM'}>Bermuda</MenuItem>
            <MenuItem value={'BT'}>Bhutan</MenuItem>
            <MenuItem value={'BO'}>Bolivia</MenuItem>
            <MenuItem value={'BA'}>Bosnia and Herzegovina</MenuItem>
            <MenuItem value={'BW'}>Botswana</MenuItem>
            <MenuItem value={'BR'}>Brazil</MenuItem>
            <MenuItem value={'BN'}>Brunei Darussalam</MenuItem>
            <MenuItem value={'BG'}>Bulgaria</MenuItem>
            <MenuItem value={'BF'}>Burkina Faso</MenuItem>
            <MenuItem value={'BI'}>Burundi</MenuItem>
            <MenuItem value={'KH'}>Cambodia</MenuItem>
            <MenuItem value={'CM'}>Cameroon</MenuItem>
            <MenuItem value={'CA'}>Canada</MenuItem>
            <MenuItem value={'CV'}>Cape Verde</MenuItem>
            <MenuItem value={'KY'}>Cayman Islands</MenuItem>
            <MenuItem value={'CF'}>Central African Republic</MenuItem>
            <MenuItem value={'TD'}>Chad</MenuItem>
            <MenuItem value={'CL'}>Chile</MenuItem>
            <MenuItem value={'CN'}>China</MenuItem>
            <MenuItem value={'CO'}>Colombia</MenuItem>
            <MenuItem value={'KM'}>Comoros</MenuItem>
            <MenuItem value={'CG'}>Congo</MenuItem>
            <MenuItem value={'CR'}>Costa Rica</MenuItem>
            <MenuItem value={'CI'}>Cote D'Ivoire</MenuItem>
            <MenuItem value={'HR'}>Croatia</MenuItem>
            <MenuItem value={'CU'}>Cuba</MenuItem>
            <MenuItem value={'CY'}>Cyprus</MenuItem>
            <MenuItem value={'CZ'}>Czech Republic</MenuItem>
            <MenuItem value={'DK'}>Denmark</MenuItem>
            <MenuItem value={'DJ'}>Djibouti</MenuItem>
            <MenuItem value={'DM'}>Dominica</MenuItem>
            <MenuItem value={'DO'}>Dominican Republic</MenuItem>
            <MenuItem value={'EC'}>Ecuador</MenuItem>
            <MenuItem value={'EG'}>Egypt</MenuItem>
            <MenuItem value={'SV'}>El Salvador</MenuItem>
            <MenuItem value={'GQ'}>Equatorial Guinea</MenuItem>
            <MenuItem value={'ER'}>Eritrea</MenuItem>
            <MenuItem value={'EE'}>Estonia</MenuItem>
            <MenuItem value={'ET'}>Ethiopia</MenuItem>
            <MenuItem value={'FJ'}>Fiji</MenuItem>
            <MenuItem value={'FI'}>Finland</MenuItem>
            <MenuItem value={'FR'}>France</MenuItem>
            <MenuItem value={'GA'}>Gabon</MenuItem>
            <MenuItem value={'GM'}>Gambia</MenuItem>
            <MenuItem value={'GE'}>Georgia</MenuItem>
            <MenuItem value={'DE'}>Germany</MenuItem>
            <MenuItem value={'GH'}>Ghana</MenuItem>
            <MenuItem value={'GR'}>Greece</MenuItem>
            <MenuItem value={'GD'}>Grenada</MenuItem>
            <MenuItem value={'GT'}>Guatemala</MenuItem>
            <MenuItem value={'GN'}>Guinea</MenuItem>
            <MenuItem value={'GW'}>Guinea-Bissau</MenuItem>
            <MenuItem value={'GY'}>Guyana</MenuItem>
            <MenuItem value={'HT'}>Haiti</MenuItem>
            <MenuItem value={'HN'}>Honduras</MenuItem>
            <MenuItem value={'HK'}>Hong Kong</MenuItem>
            <MenuItem value={'HU'}>Hungary</MenuItem>
            <MenuItem value={'IS'}>Iceland</MenuItem>
            <MenuItem value={'IN'}>India</MenuItem>
            <MenuItem value={'ID'}>Indonesia</MenuItem>
            <MenuItem value={'IR'}>Iran</MenuItem>
            <MenuItem value={'IQ'}>Iraq</MenuItem>
            <MenuItem value={'IE'}>Ireland</MenuItem>
            <MenuItem value={'IL'}>Israel</MenuItem>
            <MenuItem value={'IT'}>Italy</MenuItem>
            <MenuItem value={'JM'}>Jamaica</MenuItem>
            <MenuItem value={'JP'}>Japan</MenuItem>
            <MenuItem value={'JO'}>Jordan</MenuItem>
            <MenuItem value={'KZ'}>Kazakhstan</MenuItem>
            <MenuItem value={'KE'}>Kenya</MenuItem>
            <MenuItem value={'KI'}>Kiribati</MenuItem>
            <MenuItem value={'KP'}>North Korea</MenuItem>
            <MenuItem value={'KR'}>South Korea</MenuItem>
            <MenuItem value={'KW'}>Kuwait</MenuItem>
            <MenuItem value={'KG'}>Kyrgyzstan</MenuItem>
            <MenuItem value={'LA'}>Laos</MenuItem>
            <MenuItem value={'LV'}>Latvia</MenuItem>
            <MenuItem value={'LB'}>Lebanon</MenuItem>
            <MenuItem value={'LS'}>Lesotho</MenuItem>
            <MenuItem value={'LR'}>Liberia</MenuItem>
            <MenuItem value={'LY'}>Libya</MenuItem>
            <MenuItem value={'LI'}>Liechtenstein</MenuItem>
            <MenuItem value={'LT'}>Lithuania</MenuItem>
            <MenuItem value={'LU'}>Luxembourg</MenuItem>
            <MenuItem value={'MO'}>Macau</MenuItem>
            <MenuItem value={'MK'}>Macedonia</MenuItem>
            <MenuItem value={'MG'}>Madagascar</MenuItem>
            <MenuItem value={'MW'}>Malawi</MenuItem>
            <MenuItem value={'MY'}>Malaysia</MenuItem>
            <MenuItem value={'MV'}>Maldives</MenuItem>
            <MenuItem value={'ML'}>Mali</MenuItem>
            <MenuItem value={'MT'}>Malta</MenuItem>
            <MenuItem value={'MH'}>Marshall Islands</MenuItem>
            <MenuItem value={'MR'}>Mauritania</MenuItem>
            <MenuItem value={'MU'}>Mauritius</MenuItem>
            <MenuItem value={'MX'}>Mexico</MenuItem>
            <MenuItem value={'FM'}>Micronesia</MenuItem>
            <MenuItem value={'MD'}>Moldova</MenuItem>
            <MenuItem value={'MC'}>Monaco</MenuItem>
            <MenuItem value={'MN'}>Mongolia</MenuItem>
            <MenuItem value={'ME'}>Montenegro</MenuItem>
            <MenuItem value={'MA'}>Morocco</MenuItem>
            <MenuItem value={'MZ'}>Mozambique</MenuItem>
            <MenuItem value={'MM'}>Myanmar</MenuItem>
            <MenuItem value={'NA'}>Namibia</MenuItem>
            <MenuItem value={'NR'}>Nauru</MenuItem>
            <MenuItem value={'NP'}>Nepal</MenuItem>
            <MenuItem value={'NL'}>Netherlands</MenuItem>
            <MenuItem value={'NZ'}>New Zealand</MenuItem>
            <MenuItem value={'NI'}>Nicaragua</MenuItem>
            <MenuItem value={'NE'}>Niger</MenuItem>
            <MenuItem value={'NG'}>Nigeria</MenuItem>
            <MenuItem value={'NO'}>Norway</MenuItem>
            <MenuItem value={'OM'}>Oman</MenuItem>
            <MenuItem value={'PK'}>Pakistan</MenuItem>
            <MenuItem value={'PS'}>Palestine</MenuItem>
            <MenuItem value={'PA'}>Panama</MenuItem>
            <MenuItem value={'PG'}>Papua New Guinea</MenuItem>
            <MenuItem value={'PY'}>Paraguay</MenuItem>
            <MenuItem value={'PE'}>Peru</MenuItem>
            <MenuItem value={'PH'}>Philippines</MenuItem>
            <MenuItem value={'PL'}>Poland</MenuItem>
            <MenuItem value={'PT'}>Portugal</MenuItem>
            <MenuItem value={'QA'}>Qatar</MenuItem>
            <MenuItem value={'RO'}>Romania</MenuItem>
            <MenuItem value={'RU'}>Russia</MenuItem>
            <MenuItem value={'RW'}>Rwanda</MenuItem>
            <MenuItem value={'LC'}>Saint Lucia</MenuItem>
            <MenuItem value={'WS'}>Samoa</MenuItem>
            <MenuItem value={'SM'}>San Marino</MenuItem>
            <MenuItem value={'ST'}>Sao Tome and Principe</MenuItem>
            <MenuItem value={'SA'}>Saudi Arabia</MenuItem>
            <MenuItem value={'SN'}>Senegal</MenuItem>
            <MenuItem value={'RS'}>Serbia</MenuItem>
            <MenuItem value={'SC'}>Seychelles</MenuItem>
            <MenuItem value={'SL'}>Sierra Leone</MenuItem>
            <MenuItem value={'SG'}>Singapore</MenuItem>
            <MenuItem value={'SK'}>Slovakia</MenuItem>
            <MenuItem value={'SI'}>Slovenia</MenuItem>
            <MenuItem value={'SB'}>Solomon Islands</MenuItem>
            <MenuItem value={'SO'}>Somalia</MenuItem>
            <MenuItem value={'ZA'}>South Africa</MenuItem>
            <MenuItem value={'SS'}>South Sudan</MenuItem>
            <MenuItem value={'ES'}>Spain</MenuItem>
            <MenuItem value={'LK'}>Sri Lanka</MenuItem>
            <MenuItem value={'SD'}>Sudan</MenuItem>
            <MenuItem value={'SR'}>Suriname</MenuItem>
            <MenuItem value={'SZ'}>Swaziland</MenuItem>
            <MenuItem value={'SE'}>Sweden</MenuItem>
            <MenuItem value={'CH'}>Switzerland</MenuItem>
            <MenuItem value={'SY'}>Syria</MenuItem>
            <MenuItem value={'TW'}>Taiwan</MenuItem>
            <MenuItem value={'TJ'}>Tajikistan</MenuItem>
            <MenuItem value={'TZ'}>Tanzania</MenuItem>
            <MenuItem value={'TH'}>Thailand</MenuItem>
            <MenuItem value={'TL'}>Timor-Leste</MenuItem>
            <MenuItem value={'TG'}>Togo</MenuItem>
            <MenuItem value={'TO'}>Tonga</MenuItem>
            <MenuItem value={'TT'}>Trinidad and Tobago</MenuItem>
            <MenuItem value={'TN'}>Tunisia</MenuItem>
            <MenuItem value={'TR'}>Turkey</MenuItem>
            <MenuItem value={'TM'}>Turkmenistan</MenuItem>
            <MenuItem value={'TC'}>Turks and Caicos Islands</MenuItem>
            <MenuItem value={'TV'}>Tuvalu</MenuItem>
            <MenuItem value={'UG'}>Uganda</MenuItem>
            <MenuItem value={'UA'}>Ukraine</MenuItem>
            <MenuItem value={'AE'}>United Arab Emirates</MenuItem>
            <MenuItem value={'GB'}>United Kingdom</MenuItem>
            <MenuItem value={'US'}>United States</MenuItem>
            <MenuItem value={'UY'}>Uruguay</MenuItem>
            <MenuItem value={'UZ'}>Uzbekistan</MenuItem>
            <MenuItem value={'VU'}>Vanuatu</MenuItem>
            <MenuItem value={'VE'}>Venezuela</MenuItem>
            <MenuItem value={'YE'}>Yemen</MenuItem>
            <MenuItem value={'ZM'}>Zambia</MenuItem>
            <MenuItem value={'ZW'}>Zimbabwe</MenuItem>
          </Select>
        </StyledFormControl>
      ) : (
        <StyledFormControl variant="outlined" fullWidth>
          <TextField
            id="search-input"
            label="Search Categories"
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
          />
        </StyledFormControl>
      )}
      </Grid>
      </Grid>
      <StyledNote>
        <p>
          The processing times of IRCC, as well as all other data in the application, are sourced directly from the IRCC site. The processing times are calculated based on historical data, which means they are determined by the duration it took to process 80% of past applications. The calculation of processing time begins on the day IRCC receives a complete application and concludes when a decision is made on that application.
        </p>
      </StyledNote>
      {selectedOption === 'Outside' && (
        <Typography variant="body1">
          Last update: {lastUpdatedDate}
        </Typography>
      )}
     
      <StyledPaper elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledHeaderTableCell>Category</StyledHeaderTableCell>
              <StyledHeaderTableCell>Processing Time</StyledHeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(filteredData).map((category, index) => {
              // Exclude the 'lastupdated' key from rendering in the table.
              if (category === 'lastupdated') return null;

              return (
                <StyledTableRow key={index}>
                  <StyledTableCell>{transformCategory(category)}</StyledTableCell>
                  <StyledTableCell>{renderCellValue(filteredData[category][selectedCountry] ?? filteredData[category])}</StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledPaper>
    </StyledContainer>
  );
}

export default VietnamDataDisplay;